import http from './httpClient';

/**
 * recipeBookApi — the only place the Structured Recipe Book talks to the server.
 *
 * Every method here hits /api/recipe-book/*, a namespace that shares no code with the
 * Item Mapping save path. Nothing in this file touches /api/recipes.
 *
 * The server is the authority on validation, versions and permissions (spec §10.4,
 * §7.1); this client mirrors rules only for instant feedback, never to decide.
 */

/** Errors from httpClient carry .status and .data; normalise so callers can branch. */
const asApiError = (error) => ({
  status: error?.status ?? error?.response?.status ?? 0,
  data: error?.data ?? error?.response?.data ?? null,
  message: error?.message || 'Request failed',
});

class RecipeBookApi {
  /**
   * "Does this feature exist for me?" — answers { enabled, is_owner }.
   * Never throws: a nav bar must not break because a probe failed. A failure is
   * indistinguishable from "off", which is the safe direction (RULE 7).
   */
  static async getConfig() {
    try {
      const data = await http.get('/recipe-book/config');
      return { enabled: data?.enabled === true, isOwner: data?.is_owner === true };
    } catch (_) {
      return { enabled: false, isOwner: false };
    }
  }

  /** The whole book (spec §9.2). */
  static async getBook({ includeInactive = false } = {}) {
    const qs = includeInactive ? '?includeInactive=true' : '';
    const data = await http.get(`/recipe-book/structured${qs}`);
    if (!data || data.success === false) {
      throw new Error((data && data.error) || 'Failed to load the recipe book');
    }
    return data;
  }

  /** Blast radius for a pending change set, before anything is written (§9.4). */
  static async getImpact({ updates = [], deletes = [] }) {
    try {
      const data = await http.post('/recipe-book/structured/impact', { updates, deletes });
      return data && data.success ? data : null;
    } catch (_) {
      // The dialog degrades to "we could not work out the blast radius" rather than
      // blocking a save the user is entitled to make.
      return null;
    }
  }

  /**
   * One press of Confirm (§9.3). Throws a normalised error so the caller can
   * distinguish 409 (conflict), 422 (validation) and everything else — because
   * each keeps the user's edits and shows a different surface (§12.8).
   */
  static async saveBatch({ batchId, updates = [], deletes = [] }) {
    try {
      const data = await http.patch('/recipe-book/structured/batch', {
        batch_id: batchId,
        source: 'recipe_book',
        updates,
        deletes,
      });
      if (!data || data.success === false) {
        throw Object.assign(new Error(data?.message || 'Save failed'), { api: { status: 400, data } });
      }
      return data;
    } catch (error) {
      const api = error.api || asApiError(error);
      const wrapped = new Error(api.data?.message || api.message || 'Save failed');
      wrapped.api = api;
      throw wrapped;
    }
  }
}

export default RecipeBookApi;
